import React from 'react';
import { Box, MenuItem, TextField } from '@mui/material';
import { useSelector } from 'react-redux';

import * as ShopSelectors from 'selectors/ShopSelectors';
import { FeaturedImage as FeaturedImageType, isValidButton } from 'common/modules/customization';
import FeaturedImageUploader from './FeaturedImageUploader';
import { useTranslation } from 'services/localization';
import CollapseSection from '../components/CollapseSection';
import { getCustomizerLocaleOnChange } from '../utils';

export interface Props {
	value: FeaturedImageType;
	onChange: (value: FeaturedImageType) => void;
}

const FeaturedImageContainer = (props: Props) => {
	const { value, onChange } = props;
	const { t } = useTranslation();

	const defaultLanguage = useSelector(ShopSelectors.activeShopDefaultLanguage);

	const alignmentOptions = [
		{ label: t('common:position.top', 'Top'), value: 'top' },
		{ label: t('common:position.middle', 'Middle'), value: 'center' },
		{ label: t('common:position.bottom', 'Bottom'), value: 'bottom' },
	];

	const { valid, message } = isValidButton(value?.button ?? null, t);

	return (
		<CollapseSection
			id="feature-image-section"
			heading={t('channels.online.featuredImage.header', 'Featured image')}
		>
			<FeaturedImageUploader />
			<Box mt={3}>
				<TextField
					select
					value={value?.alignment}
					defaultValue="center"
					onChange={(event) =>
						onChange({
							...value,
							alignment: event.target.value as 'center' | 'top' | 'bottom',
						})
					}
					label={t('channels.online.featuredImage.alignmentLabel', 'Image alignment')}
					InputLabelProps={{
						shrink: true,
					}}
					fullWidth
				>
					{alignmentOptions.map((option) => (
						<MenuItem key={option.value} value={option.value}>
							{option.label}
						</MenuItem>
					))}
				</TextField>
			</Box>
			<Box mt={3}>
				<TextField
					value={value?.heading?.def}
					onChange={(event) =>
						onChange({
							...value,
							heading: getCustomizerLocaleOnChange(
								event.target.value,
								value?.heading,
								defaultLanguage,
							),
						})
					}
					label={t('channels.online.featuredImage.headingLabel', 'Heading')}
					placeholder={t('channels.online.featuredImage.headingPlaceholder', 'Add a page heading')}
					InputLabelProps={{
						shrink: true,
					}}
					fullWidth
				/>
			</Box>
			<Box mt={3}>
				<TextField
					value={value?.description?.def}
					onChange={(event) =>
						onChange({
							...value,
							description: getCustomizerLocaleOnChange(
								event.target.value,
								value?.description,
								defaultLanguage,
							),
						})
					}
					label={t('channels.online.featuredImage.descriptionLabel', 'Description')}
					placeholder={t(
						'channels.online.featuredImage.descriptionPlaceholder',
						'Add a page description',
					)}
					InputLabelProps={{
						shrink: true,
					}}
					fullWidth
				/>
			</Box>
			<Box mt={3}>
				<TextField
					value={value?.button?.label?.def}
					onChange={(event) =>
						onChange({
							...value,
							button: {
								label: getCustomizerLocaleOnChange(
									event.target.value,
									value.button?.label,
									defaultLanguage,
								),
								src: value?.button?.src ?? undefined,
							},
						})
					}
					label={t('channels.online.featuredImage.buttonLabel', 'Button label')}
					placeholder={t('channels.online.featuredImage.buttonPlaceholder', 'Add a label')}
					InputLabelProps={{
						shrink: true,
					}}
					fullWidth
					error={!!value?.button?.label?.def && !valid}
					helperText={!value?.button?.src?.length && !valid && message}
				/>
			</Box>
			<Box mt={3}>
				<TextField
					value={value?.button?.src}
					onChange={(event) =>
						onChange({
							...value,
							button: {
								src: !!event.target.value ? event.target.value : undefined,
								label: value?.button?.label ?? undefined,
							},
						})
					}
					label={t('channels.online.featuredImage.buttonUrlLabel', 'Button url')}
					placeholder={t('common:form.addUrl', 'Add a URL')}
					InputLabelProps={{
						shrink: true,
					}}
					fullWidth
					error={!!value?.button?.src?.length && !valid}
					helperText={!!value?.button?.src?.length && !valid && message}
				/>
			</Box>
		</CollapseSection>
	);
};

export default FeaturedImageContainer;
